import { useQuery, useMutation } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { get } from 'lodash';
import {
  queryAppData,
  getTimelineSummary,
  getTimelineSessions,
  getTimelineEventList,
  getTimelineImpactedSessions,
  getFilter4UserTimeline,
  getTimelineEventListNew,
} from '@api/appData';
import { querySelector } from '@store/metricsChartContainer/selectors';
import instance from '@api/axiosInstance';
import { customerOverrideForDrilldown } from '@src/components/app-viewer-sessions/utils';
import {
  ALL_FLOWS_IDENTIFIER,
  CLIENT_ID_FIELD,
  DEVICE_NAME_FIELD,
  FLOW_END_TIMESTAMP_FIELD,
  FLOW_START_TIMESTAMP_FIELD,
  USER_ID_FIELD,
} from '../constants';
import { post } from '@src/api/sharing';
import { EQueryIdType } from '@src/pages/AppTimeline/types';
import { getUserProfile } from '@src/store/common/selectors';
import { appendFlowIdentifier } from '@src/utils/filter';
import getMetricsData from '@src/api/metricContainer';
import { DATASET } from '@src/utils/constants';
import type { FilterRule } from '@src/components/filter/types';
import type { AppFlowMetadata } from '@src/hooks/flow/useFlowConfigs';

const EmptyArray: any[] = [];

const useGetTimelineSummary = (viewerId: string[], interval: string, clientId: string | null) => {
  const { reqBody } = useSelector(querySelector);
  const clonedViewerId = [...viewerId];
  const viewerIdKey = JSON.stringify(clonedViewerId.sort());
  return useQuery(
    [viewerIdKey, interval, clientId, 'query_summary'],
    async () => {
      if (!interval) {
        return {
          userInfoData: [],
          eventSummaryData: [],
        };
      }
      const res = await getTimelineSummary(reqBody, {
        viewerId,
        ts: interval,
        clientId,
      });
      return res?.data;
    },
    {
      enabled: !!clientId && viewerId.length > 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

const useGetTimelineSessions = (
  viewerId: string[] = [],
  interval: string,
  clientId: string | null,
  fields: string[],
  enabled = true,
) => {
  const { reqBody } = useSelector(querySelector);
  const clonedFields = [...fields];
  const fieldsKey = JSON.stringify(clonedFields.sort());
  const clonedViewerId = [...viewerId];
  const viewerIdKey = JSON.stringify(clonedViewerId.sort());
  return useQuery(
    [viewerIdKey, interval, clientId, 'query_sessions', fieldsKey],
    async () => {
      if (!interval) {
        return EmptyArray;
      }
      const res = await getTimelineSessions(reqBody, {
        viewerId,
        ts: interval,
        clientId,
        fields,
      });
      return res?.data;
    },
    {
      enabled: enabled && !!clientId && viewerId.length > 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

// TODO: Deprecate
export const useGetTimelineEventList = (
  viewerId: string[] = [],
  interval: string,
  clientId: string | null,
  sessionId: string,
  enableNW: boolean,
  globalFilters: any,
  enableUserId: boolean,
  flowMetadata?: AppFlowMetadata,
) => {
  const { customer_id } = useSelector(getUserProfile);
  const { reqBody } = useSelector(querySelector);
  const gfText = JSON.stringify(globalFilters);
  const clonedViewerId = [...viewerId];
  const viewerIdKey = JSON.stringify(clonedViewerId.sort());
  const isFlowTimeline = !!flowMetadata;
  const startFlowEventName = flowMetadata?.definition.events.start_event?.name;
  const endFlowEventName = flowMetadata?.definition.events.success_event?.name;

  return useQuery(
    [
      viewerIdKey,
      interval,
      clientId,
      sessionId,
      `network-request: ${enableNW}`,
      gfText,
      `user-id: ${enableUserId}`,
      'query_event_list',
      [startFlowEventName, endFlowEventName],
    ],
    async () => {
      if (!interval || !sessionId) {
        return {
          eventList: [],
          requestBody: {},
        };
      }
      const res = await getTimelineEventList(reqBody, {
        viewerId,
        ts: interval,
        clientId,
        sessionId,
        enableNW,
        globalFilters,
        enableUserId,
        customer_id,
        isFlowTimeline,
      });

      if (isFlowTimeline && res?.data?.eventList?.length) {
        // const startIndex = res.data.eventList.findIndex(
        //   (event) => event.eventName === startFlowEventName,
        // );
        // const endIndex = res.data.eventList.findIndex(
        //   (event, index) => event.eventName === endFlowEventName && index > startIndex,
        // );

        return {
          ...res.data,
          // eventList: res.data.eventList.slice(
          //   startIndex === -1 ? 0 : startIndex,
          //   // If endIndex is -1, it means the end event was not found, the flow instance isn't complete,
          //   // so including all events after the start event.
          //   endIndex + 1 || undefined,
          // ),
        };
      }

      return res?.data;
    },
    {
      enabled: !!sessionId && !!clientId && viewerId.length > 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

const useGetTimelineEventListNew = (
  viewerId: string[] = [],
  interval: string,
  clientId: string | null,
  sessionId: string | string[],
  enableNW: boolean,
  globalFilters: any,
  enableUserId: boolean,
) => {
  const { customer_id } = useSelector(getUserProfile);
  const { reqBody } = useSelector(querySelector);
  const gfText = JSON.stringify(globalFilters);
  const clonedViewerId = [...viewerId];
  const viewerIdKey = JSON.stringify(clonedViewerId.sort());

  return useQuery(
    [
      viewerIdKey,
      interval,
      clientId,
      sessionId,
      `network-request: ${enableNW}`,
      gfText,
      `user-id: ${enableUserId}`,
      'query_event_list',
    ],
    async () => {
      if (!interval || !sessionId || sessionId.length === 0) {
        return {
          eventList: [],
          requestBody: {},
        };
      }
      const res = await getTimelineEventListNew(reqBody, {
        viewerId,
        ts: interval,
        clientId,
        sessionId,
        enableNW,
        globalFilters,
        enableUserId,
        customer_id,
      });

      return res?.data;
    },
    {
      enabled: !!sessionId && !!clientId && viewerId.length > 0,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

export interface GetImpactedSessionIdsParams {
  viewerId?: string[];
  clientId: string;
  interval: string;
  globalFilters: any;
  eventNames: string[];
  enabled: boolean;
}

const useGetImpactedSessionIds = ({
  enabled,
  viewerId = [],
  clientId,
  interval,
  globalFilters,
  eventNames = [],
}: GetImpactedSessionIdsParams) => {
  const { reqBody } = useSelector(querySelector);
  const clonedViewerId = [...viewerId];
  const viewerIdKey = JSON.stringify(clonedViewerId.sort());
  const eventNamesKey = JSON.stringify(eventNames.sort());

  return useQuery(
    [globalFilters, interval, clientId, viewerIdKey, eventNamesKey, 'query_impacted_session_ids'],
    async () => {
      if (!interval) {
        return EmptyArray;
      }

      const res = await getTimelineImpactedSessions(reqBody, {
        viewerId,
        ts: interval,
        clientId,
        globalFilters,
        eventNames,
      });
      return res?.data;
    },
    {
      enabled: enabled && !!clientId && !!interval && viewerId.length > 0,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

export const getRequestBody = (options: {
  interval: string;
  selectedTrendMetric: string;
  groupBy: string[];
  filter: any[][];
}) => {
  const { selectedTrendMetric, interval, groupBy, filter } = options;
  return [
    {
      type: 'group-by',
      dataset: 'AppExperience',
      metrics: [selectedTrendMetric],
      interval,
      granularity: 'ALL',
      filter,
      limit: 1500,
      groupBy,
      orderBy: 'desc',
      sortBy: [{ metric: selectedTrendMetric, order: 'desc' }],
      options: {
        withTotals: false,
      },
    },
  ];
};

const useGetTimelineViewerIds = (
  idType: EQueryIdType,
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
  flowIdentifier?: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = customerOverrideForDrilldown[customerId] || USER_ID_FIELD;
  const baseFilter = [
    [
      {
        field: dimensionAppUserId,
        op: 'not_eq',
        // Pass value != '' and display != '' to exclude empty or unknown user IDs
        value: '',
        display: '',
      },
    ],
  ];
  const filter = flowIdentifier ? appendFlowIdentifier(baseFilter, flowIdentifier) : baseFilter;
  const groupBy = [dimensionAppUserId];

  return useQuery(
    [selectedTrendMetric, interval, 'query_viewer_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          interval,
          filter,
          selectedTrendMetric,
          groupBy,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        const data = get(d, 'data.0.data.0.results', []);
        const arr = data.map((e) => e[dimensionAppUserId]);
        return arr;
      });
      return res;
    },
    { enabled: idType === EQueryIdType.userId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const useGetTimelineClientIds = (
  idType: EQueryIdType,
  selectedTrendMetric: string,
  interval: string,
  flowIdentifier?: string,
) => {
  const source = axios.CancelToken.source();
  const baseFilter = [
    [
      {
        field: CLIENT_ID_FIELD,
        op: 'not_eq',
        // Pass value != '' and display != '' to exclude empty or unknown client IDs
        value: '',
        display: '',
      },
    ],
  ];
  const filter = flowIdentifier ? appendFlowIdentifier(baseFilter, flowIdentifier) : baseFilter;
  const groupBy = [CLIENT_ID_FIELD];

  return useQuery(
    [selectedTrendMetric, interval, 'query_client_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          interval,
          selectedTrendMetric,
          filter,
          groupBy,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    { enabled: idType === EQueryIdType.clientId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const useGetClientIdsByViewerId = (
  idType: EQueryIdType,
  viewerId: string[] = [],
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
  flowIdentifier?: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = customerOverrideForDrilldown[customerId] || USER_ID_FIELD;
  const groupBy = [CLIENT_ID_FIELD, DEVICE_NAME_FIELD];
  const baseFilter = [
    [
      {
        field: dimensionAppUserId,
        op: 'eq',
        value: viewerId[0],
      },
    ],
  ];
  const filter = flowIdentifier ? appendFlowIdentifier(baseFilter, flowIdentifier) : baseFilter;

  return useQuery(
    [viewerId, selectedTrendMetric, interval, 'query_client_id_by_viewer_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          interval,
          selectedTrendMetric,
          filter,
          groupBy,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    {
      enabled: idType === EQueryIdType.userId && viewerId.length > 0,
      retryOnMount: false,
      refetchOnWindowFocus: false,
    },
  );
};

const useGetViewerIdsByClientId = (
  idType: EQueryIdType,
  clientId: string,
  selectedTrendMetric: string,
  interval: string,
  customerId: string,
  flowIdentifier?: string,
) => {
  const source = axios.CancelToken.source();
  const dimensionAppUserId = customerOverrideForDrilldown[customerId] || USER_ID_FIELD;
  const groupBy = [dimensionAppUserId, DEVICE_NAME_FIELD];
  const baseFilter = [
    [
      {
        field: CLIENT_ID_FIELD,
        op: 'eq',
        value: clientId,
      },
    ],
  ];
  const filter = flowIdentifier ? appendFlowIdentifier(baseFilter, flowIdentifier) : baseFilter;

  return useQuery(
    [clientId, selectedTrendMetric, interval, 'query_viewer_id_by_client_id'],
    async () => {
      if (!interval) {
        return [];
      }
      const res = await queryAppData(
        getRequestBody({
          interval,
          selectedTrendMetric,
          filter,
          groupBy,
        }),
        {
          cancelToken: source.token,
        },
      ).then((d) => {
        return get(d, 'data.0.data.0.results', []);
      });
      return res;
    },
    { enabled: idType === EQueryIdType.clientId, retryOnMount: false, refetchOnWindowFocus: false },
  );
};

const usePostShare = () => {
  return useMutation(async (reqBody: any) => {
    const res = await post(reqBody);
    return res?.data;
  });
};

export interface GetFlowIndexParams {
  dimensions: string[];
  metrics: string[];
  interval: string;
  viewerId: string[];
  clientId: string;
  flowIdentifier?: string;
  filter?: FilterRule[][];
  enabled: boolean;
}

export const useGetFlowIndex = (params: GetFlowIndexParams) => {
  const {
    dimensions,
    metrics,
    interval,
    viewerId,
    clientId,
    flowIdentifier,
    filter = [],
    enabled,
  } = params;

  return useQuery({
    queryKey: [
      'flow-timeline',
      'index',
      { dimensions, metrics, interval, viewerId, clientId, flowIdentifier, filter },
    ],
    queryFn: () =>
      getMetricsData({
        queries: [
          {
            dataset: DATASET,
            context: window.conviva.context,
            type: 'group-by',
            isLiveMode: false,
            interval,
            granularity: 'ALL',
            filter: getFilter4UserTimeline(viewerId, clientId, flowIdentifier, filter),
            groupBy: dimensions,
            metrics,
            orderBy: 'asc',
            sortBy: [
              {
                metric: FLOW_START_TIMESTAMP_FIELD,
                order: 'asc',
              },
              {
                metric: FLOW_END_TIMESTAMP_FIELD,
                order: 'asc',
              },
            ],
            limit: 200,
            options: {
              withTotals: false,
            },
          },
        ],
      }).then((res) => res.data),
    enabled: enabled && !!flowIdentifier && !!clientId && viewerId.length > 0,
  });
};

export const useGetAllFlowInstances = (
  params: Omit<GetFlowIndexParams, 'dimensions' | 'metrics' | 'flowIdentifier'>,
) => {
  const { interval, viewerId = [], clientId, filter = [], enabled } = params;

  return useQuery({
    queryKey: ['all-flow-instances', { interval, viewerId, clientId, filter }],
    queryFn: () =>
      getMetricsData({
        queries: [
          {
            dataset: DATASET,
            context: window.conviva.context,
            type: 'group-by',
            isLiveMode: false,
            interval,
            granularity: 'ALL',
            filter: getFilter4UserTimeline(viewerId, clientId, ALL_FLOWS_IDENTIFIER, filter),
            groupBy: ['flow_identifier', FLOW_START_TIMESTAMP_FIELD, FLOW_END_TIMESTAMP_FIELD],
            metrics: [],
            orderBy: 'asc',
            sortBy: [
              {
                metric: FLOW_START_TIMESTAMP_FIELD,
                order: 'asc',
              },
              {
                metric: FLOW_END_TIMESTAMP_FIELD,
                order: 'asc',
              },
            ],
            limit: 200,
            options: {
              withTotals: false,
              dataScope: 'event',
            },
          },
        ],
      }).then((res) => res.data),
    enabled: enabled && !!clientId && viewerId.length > 0,
  });
};

export {
  useGetTimelineSummary,
  useGetTimelineViewerIds,
  useGetClientIdsByViewerId,
  useGetTimelineClientIds,
  useGetViewerIdsByClientId,
  usePostShare,
  useGetImpactedSessionIds,
  useGetTimelineSessions,
  useGetTimelineEventListNew,
};
